import styled from 'styled-components';

import Button from 'components/shared/Button';
import StandardModal, { ColorThemeName } from 'components/shared/StandardModal';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { HighCostMedicationList } from 'models/HighCostMedicationList';
import { destroyAdminHighCostMedicationList } from 'services/api/admin/highCostMedicationLists';
import { useToastActions } from 'stores/toastStore';
import { BodySmall } from 'styles/typography';

export type DeleteHighCostMedListModalProps = {
  highCostMedicationList: HighCostMedicationList;
  onClose: () => void;
};

// Admin "Remove" confirmation. Soft-archives the whole list; nothing is hard-deleted, and the
// list's medications and cost tiers are left intact. Archiving frees the client to receive a fresh
// CSV upload, which is the expected annual pharmacy-contract rollover.
export default function DeleteHighCostMedListModal({
  highCostMedicationList,
  onClose,
}: DeleteHighCostMedListModalProps) {
  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();

  const clientName = highCostMedicationList.client?.name;

  const { mutate, isPending } = useMutateWithToast({
    mutationFn: () => destroyAdminHighCostMedicationList(highCostMedicationList.id),
  });

  const handleDelete = () => {
    mutate(undefined, {
      onSuccess: () => {
        invalidateQueries({ adminHighCostMedicationLists: true, highCostMedListEligibleClients: true });
        addToast({ text: `${clientName ?? 'Client'}’s high cost medication list removed` });
        onClose();
      },
    });
  };

  return (
    <StandardModal title='Remove High Cost Medication List?' colorTheme={ColorThemeName.PrimaryRed} onCancel={onClose}>
      <Content>
        Removing {clientName ?? 'this client'}&rsquo;s high cost medication list takes it out of use. Medications on it
        will no longer be checked against incoming referrals.
      </Content>
      <StandardModal.Actions>
        <Button variant='ghost' onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button variant='danger' onClick={handleDelete} loading={isPending} disabled={isPending}>
          Remove High Cost Medication List
        </Button>
      </StandardModal.Actions>
    </StandardModal>
  );
}

const Content = styled(BodySmall)`
  display: block;
  margin-bottom: 40px;
`;
